import type { Offset } from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { migrationUiSelector } from '@store/migrationSelectors/uiSelectors'
import { socket } from '@store/socketSaga'
import type { AppState } from '@store/types'
import { type RefObject, useMemo } from 'react'
import { shallowEqual, useSelector } from 'react-redux'

import { useEventListener } from '@/hooks'
import useSpyMode from '@/hooks/useSpyMode'

const throttle = (fn: Function, delay: number) => {
  let lastCall = 0
  return (...args: any[]) => {
    const now = Date.now()
    if (now - lastCall >= delay) {
      lastCall = now
      fn(...args)
    }
  }
}

export const useCursorTracking = (
  containerRef: RefObject<HTMLDivElement | null>
) => {
  const projectId = useSelector(migrationProjectSelector.getProjectId)
  const modalType = useSelector(migrationUiSelector.getModalType)
  const scale = useSelector<AppState, number>(
    ({ canvas }) => canvas.scale,
    shallowEqual
  )

  const { isSpyMode } = useSpyMode()

  const getRelativeMousePosition = (e: MouseEvent): Offset | null => {
    if (!containerRef.current) {
      return null
    }

    const rect = containerRef.current.getBoundingClientRect()

    const relativeX = e.clientX - rect.left
    const relativeY = e.clientY - rect.top

    return {
      x: relativeX / scale,
      y: relativeY / scale,
    }
  }

  const throttledEmitCursorPosition = useMemo(
    () =>
      throttle((x: number, y: number) => {
        if (isSpyMode || modalType !== 'MODAL_NONE') {
          return
        }

        socket.emit('project:collaborator', projectId, { x, y })
      }, 120),
    [projectId, modalType, isSpyMode]
  )

  useEventListener(
    'mousemove',
    (e: MouseEvent) => {
      const position = getRelativeMousePosition(e)
      if (position) {
        throttledEmitCursorPosition(position.x, position.y)
      }
    },
    containerRef.current
  )
}
